import { Box } from '@mui/material';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Rectangle,
  ReferenceLine,
} from 'recharts';
import { FlexBox } from 'components/Containers/FlexBox';
import { ThemePalette } from 'mui.theme';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import { formatNumberToShortScale } from 'views/Production/Loader/component/utils';
import { truncateTo2dp } from 'utils/helpers/general';
import { useAppSelector } from 'store/hook';
import { getDataUnit } from 'views/Production/Hauler/component/utils';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  selectedSource: string;
  customerCode: string;
  unitSystem: string;
  loaderDeviceId: string;
  height?: string;
}

const AssetItemChart = ({
  enableDarkTheme,
  selectedSource,
  customerCode,
  unitSystem,
  loaderDeviceId,
  height = '230px',
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const {
    data: {
      loaders: { devices },
    },
  } = useAppSelector((state) => state.productionReducer);

  let dataUnit = getDataUnit(selectedSource, unitSystem);
  dataUnit = dataUnit ? `(${dataUnit})` : '';

  const formattedChartData = devices
    .filter((item) => item.loader === loaderDeviceId)
    .map((entry) => {
      let value = 0;

      switch (selectedSource) {
        case 'Production':
          value = entry.total_load_for_selected_range;
          break;
        case 'Production Per Hour':
          value = entry.device_loads_per_hr;
          break;
        case 'Load Per Day':
          value = entry.device_loads_per_day;
          break;
        case 'Hauler Load Time':
          value = isNaN(entry?.device_hauler_load_time)
            ? 0
            : Number(entry.device_hauler_load_time) / 60;
          break;
        case 'Loader Cycle Time':
          value = isNaN(entry.device_loader_cycle_time)
            ? 0
            : Number(entry.device_loader_cycle_time) / 60;
          break;
        default:
          value = 0;
      }

      return {
        date: entry.day,
        value: convertToTwoDecimalPlaces(`${value}`),
      };
    });

  const chartData = formattedChartData.sort((a, b) => {
    const dateA = dayjs(a.date).unix();
    const dateB = dayjs(b.date).unix();
    return dateA - dateB;
  });

  const formatTimestamp = (timestamp: string): string =>
    dayjs(timestamp).format('DD MMM');

  const validEntries = chartData.filter(
    (entry) =>
      entry.value !== null && !isNaN(+entry.value) && +entry.value !== 0
  );

  const average =
    selectedSource === 'Production Per Hour'
      ? validEntries.reduce((sum, entry) => {
          const device = devices.find(
            (d) => d.day === entry.date && d.loader === loaderDeviceId
          );
          return sum + (device?.total_load_for_selected_range ?? 0);
        }, 0) /
        (validEntries.reduce((sum, entry) => {
          const device = devices.find(
            (d) => d.day === entry.date && d.loader === loaderDeviceId
          );
          return sum + (device?.engine_active_minute ?? 0) / 60; // Convert to hours
        }, 0) || 1) // Avoid division by zero
      : validEntries.reduce(
          (sum: number, entry) => sum + parseFloat(entry.value),
          0
        ) / (validEntries.length || 1);

  const maxValue = validEntries.length
    ? Math.max(...validEntries.map((entry) => parseFloat(entry.value)))
    : 0;

  return (
    <Box sx={{ paddingTop: '10px' }}>
      <FlexBox
        sx={{
          maxWidth: 'inherit',
          height,
          paddingTop: '20px',
          marginRight: '10px',
          paddingBottom: '10px',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={700}
            height={300}
            data={chartData}
            margin={{ top: 20, right: 100, left: 5, bottom: 5 }}
            barSize={30}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={ThemePalette.dark.cartesianGrid}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickFormatter={formatTimestamp}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              domain={[0, Math.ceil(maxValue * 1.1)]}
              label={{
                value: ` ${
                  selectedSource.toLowerCase().replace(/ /g, '') ===
                  'haulerloadtime'
                    ? `${selectedSource} (mins)`
                    : selectedSource.toLowerCase().replace(/ /g, '') ===
                      'loadercycletime'
                    ? `${selectedSource} (mins)`
                    : `${selectedSource} ${dataUnit}`
                }`,
                angle: 270,
                position: 'insideLeft',
                fontSize: 10,
                textAnchor: 'middle',
                dy: 50,
              }}
            />
            <Tooltip
              cursor={{ fill: enableDarkTheme ? '#1B2B4A' : '#CCCCCC' }}
              wrapperStyle={{ outline: 'none', fontSize: '11px' }}
              contentStyle={{
                backgroundColor: enableDarkTheme
                  ? 'rgba(0,0,0, 0.6)'
                  : 'rgba(255, 255, 255, 0.6)',
                borderColor: 'transparent',
                borderRadius: '4px',
              }}
              itemStyle={{
                color: enableDarkTheme
                  ? ThemePalette.typography.wheat
                  : ThemePalette.typography.black,
              }}
            />
            <Bar
              dataKey="value"
              fill="#4A7BFA"
              activeBar={<Rectangle fill="pink" stroke="blue" />}
            />
            <ReferenceLine
              y={average}
              label={{
                value:
                  selectedSource.toLowerCase().replace(/ /g, '') ===
                  'productionperhour'
                    ? `${average.toFixed(2)} ${dataUnit}`
                    : selectedSource.toLowerCase().replace(/ /g, '') ===
                        'haulerloadtime' ||
                      selectedSource.toLowerCase().replace(/ /g, '') ===
                        'loadercycletime'
                    ? `${formatNumberToShortScale(
                        truncateTo2dp(average.toString())
                      )} mins`
                    : `Avg ${average.toFixed(2)}  ${dataUnit}`,
                position: 'right',
                fontSize: '10px',
              }}
              stroke="#4A7BFA"
              strokeDasharray="3 3"
              isFront
            />
          </BarChart>
        </ResponsiveContainer>
      </FlexBox>
    </Box>
  );
};

export default AssetItemChart;
